import { CANONICAL_ADDRESSES } from "@atelier/abis";
import { ZERO_ADDRESS, type ChainConfig, type ChainMeta } from "./chains";

/**
 * Parse comma-separated chain ids ("63,61") into numeric ids.
 * Non-numeric entries are dropped silently.
 */
export function parseChainList(raw: string | undefined | null): number[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n > 0);
}

/**
 * Build a ChainMeta from env vars for a single chainId.
 * Required: NAME, SHORT, RPC, SYMBOL, DECIMALS.
 * Optional: EXPLORER, IPFS_GATEWAY, NATIVE_NAME (defaults to SYMBOL).
 *
 * Returns null if any required field is missing.
 */
export function parseChainMeta(
  env: Record<string, string | undefined>,
  chainId: number,
  prefix = "",
): ChainMeta | null {
  const k = (suffix: string): string => `${prefix}CHAIN_${chainId}_${suffix}`;

  const name = env[k("NAME")];
  const shortRaw = env[k("SHORT")];
  const rpcUrl = env[k("RPC")];
  const symbol = env[k("SYMBOL")];
  const decimalsRaw = env[k("DECIMALS")];

  if (!name || !shortRaw || !rpcUrl || !symbol || !decimalsRaw) return null;

  const decimals = Number(decimalsRaw);
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 36) return null;

  const shortName = shortRaw.toLowerCase();
  if (!/^[a-z0-9-]+$/u.test(shortName)) return null;

  const explorerUrl = env[k("EXPLORER")] ?? "";
  const ipfsGateway = env[k("IPFS_GATEWAY")];
  const nativeName = env[k("NATIVE_NAME")] ?? symbol;

  return {
    chainId,
    name,
    shortName,
    rpcUrl,
    explorerUrl,
    nativeCurrency: { name: nativeName, symbol, decimals },
    ...(ipfsGateway ? { ipfsGateway } : {}),
    enabled: true,
  };
}

/**
 * Layer the canonical CREATE2 contract addresses onto a ChainMeta.
 * If the addresses haven't been generated yet (export-abis not run),
 * the chain is forced to `enabled=false`.
 */
export function toChainConfig(meta: ChainMeta): ChainConfig {
  const factory = CANONICAL_ADDRESSES.factory ?? ZERO_ADDRESS;
  const dropImpl = CANONICAL_ADDRESSES.dropImpl ?? ZERO_ADDRESS;
  const simpleImpl = CANONICAL_ADDRESSES.simpleImpl ?? ZERO_ADDRESS;
  const atelier1of1 = CANONICAL_ADDRESSES.atelier1of1 ?? ZERO_ADDRESS;
  const hasDeployment =
    factory !== ZERO_ADDRESS && dropImpl !== ZERO_ADDRESS && simpleImpl !== ZERO_ADDRESS;
  return {
    ...meta,
    contracts: {
      erc721Factory: factory,
      erc721DropImpl: dropImpl,
      erc721SimpleImpl: simpleImpl,
      ...(atelier1of1 !== ZERO_ADDRESS ? { atelier1of1 } : {}),
    },
    enabled: meta.enabled && hasDeployment,
  };
}

/**
 * Read chain config from an arbitrary env map.
 *
 * Conventions (all required except where noted):
 *   - `<prefix>CHAINS`            comma-separated list of chainIds to enable
 *   - `<prefix>CHAIN_<id>_NAME`   display name
 *   - `<prefix>CHAIN_<id>_SHORT`  url-safe short name
 *   - `<prefix>CHAIN_<id>_RPC`    RPC endpoint
 *   - `<prefix>CHAIN_<id>_SYMBOL` native currency symbol
 *   - `<prefix>CHAIN_<id>_DECIMALS` native currency decimals
 *   - `<prefix>CHAIN_<id>_EXPLORER`     (optional) block explorer
 *   - `<prefix>CHAIN_<id>_IPFS_GATEWAY` (optional)
 *   - `<prefix>CHAIN_<id>_NATIVE_NAME`  (optional, defaults to SYMBOL)
 *
 * `<prefix>` is `""` for workers / node / contracts scripts and `"VITE_"` for the SPA.
 *
 * Malformed or incomplete chain blocks are skipped silently.
 */
export function chainsFromEnv(
  env: Record<string, string | undefined>,
  prefix = "",
): ChainConfig[] {
  const ids = parseChainList(env[`${prefix}CHAINS`]);
  const out: ChainConfig[] = [];
  for (const id of ids) {
    const meta = parseChainMeta(env, id, prefix);
    if (!meta) continue;
    out.push(toChainConfig(meta));
  }
  return out;
}

export function enabledChainsFromEnv(
  env: Record<string, string | undefined>,
  prefix = "",
): ChainConfig[] {
  return chainsFromEnv(env, prefix).filter((c) => c.enabled);
}

export function findChainById(
  chains: ChainConfig[],
  chainId: number,
): ChainConfig | undefined {
  return chains.find((c) => c.chainId === chainId);
}

export function findChainByShortName(
  chains: ChainConfig[],
  shortName: string,
): ChainConfig | undefined {
  const s = shortName.toLowerCase();
  return chains.find((c) => c.shortName === s);
}
